import dayjs from 'dayjs';

/**
 * Put dates back into a restored stash as dayjs objects.
 *
 * A stash is `JSON.stringify`'d into localStorage (`SessionGuardContext.writeStash`).
 * dayjs implements `toJSON()`, so every dayjs in a form's values comes back out
 * of `JSON.parse` as an ISO STRING. Handing that string to `setFieldsValue` puts
 * it inside `<DatePicker>`, and rc-picker calls `.isValid()` on it:
 *
 *   TypeError: date.isValid is not a function
 *     at Object.isValidate (rc-picker/generate/dayjs.js:187)
 *
 * A parse failure would NOT have done this — `dayjs('nonsense')` is an
 * Invalid-Date dayjs and still has `.isValid()`. Only a value that never went
 * through `dayjs()` throws, so the round trip is the defect, not the input.
 *
 * ── Why the caller names the fields ───────────────────────────────────────
 *
 * `fields` is explicit rather than sniffed. A heuristic that converted anything
 * ISO-shaped would turn a note reading "customer said 2026-08-12 works" into a
 * dayjs and write garbage back to the server. Only a field actually bound to a
 * DatePicker belongs in the list.
 */
export function rehydrateStashedDates<T extends Record<string, any>>(
  values: T,
  fields: readonly string[],
): T {
  if (!values || typeof values !== 'object') return values;

  const out: Record<string, any> = { ...values };

  for (const field of fields) {
    if (!(field in out)) continue;

    const raw = out[field];

    // Untouched stays untouched. `dayjs(undefined)` is the CURRENT TIME, so
    // coercing here would stamp today's date onto a field the user left empty.
    if (raw === null || raw === undefined || raw === '') continue;

    if (dayjs.isDayjs(raw)) continue;

    const parsed = dayjs(raw);
    // An Invalid-Date dayjs would not crash the picker, but it renders as
    // "Invalid Date" in the field. Empty is the honest restore.
    out[field] = parsed.isValid() ? parsed : null;
  }

  return out as T;
}
